import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { usePageConfig } from '@/contexts/PageConfigContext';
import { USER_ROLES } from '@/constants';
import { ContentLayout } from '@/layouts/ContentLayout';
import AdminNav from '@/widgets/AdminNav';
import { AdminLayoutContext } from './context';
import type { AdminLayoutContextType } from './context';
import UsersTab from '@/pages/admin/UsersTab';
import SettingsTab from '@/pages/admin/SettingsTab';
import LeaguesTab from '@/pages/admin/LeaguesTab';
import BookmakersTab from '@/pages/admin/BookmakersTab';
import LeagueBookmakersTab from '@/pages/admin/LeagueBookmakersTab';
import MatchesTab from '@/pages/admin/MatchesTab';
import DebugApiTab from '@/pages/admin/DebugApiTab';

const AdminLayout: React.FC = () => {
  const { t } = useTranslation(['admin']);
  const { user } = useAuth();

  const isAdmin = Boolean(user?.is_admin === true || user?.role === USER_ROLES.ADMIN);

  usePageConfig({
    title: t('admin:adminPanel'),
    headerSolid: true,
  });

  const sidebar = <AdminNav />;

  const tabs = useMemo(() => [
    { component: UsersTab, path: '/admin/users' },
    { component: SettingsTab, path: '/admin/bp-settings' },
    { component: LeaguesTab, path: '/admin/leagues' },
    { component: BookmakersTab, path: '/admin/bookmakers' },
    { component: LeagueBookmakersTab, path: '/admin/league-bookmakers' },
    { component: MatchesTab, path: '/admin/matches' },
    { component: DebugApiTab, path: '/admin/debug-api' },
  ], []);

  const contextValue: AdminLayoutContextType = useMemo(() => ({
    user,
    isAdmin,
  }), [user, isAdmin]);

  return (
    <AdminLayoutContext.Provider value={contextValue}>
      <ContentLayout
        sidebar={sidebar}
        tabs={tabs}
      />
    </AdminLayoutContext.Provider>
  );
};

export default AdminLayout;
